import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import createDOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCameraRetro,
  FaExclamationTriangle,
  FaImages,
} from "react-icons/fa";
import {
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiImage,
  FiMaximize2,
  FiX,
} from "react-icons/fi";

import { getAllGalleries } from "../../services/gallerieServices";
import { useUser } from "../Home/UserContext";
import Reveal from "../../components/Reveal/Reveal";
import { toAbsolutePhotoUrl as toAbsPhoto } from "../../services/albumPhotoServices";

const FALLBACK = "/img/no-image.jpg";
const MAX_HOME_ALBUMS = 3;
const MAX_PREVIEW_PHOTOS = 12;

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* -------------------------------------------------------
   DOMPurify et styles enrichis sécurisés
-------------------------------------------------------- */

const purify =
  typeof window !== "undefined" ? createDOMPurify(window) : null;

const isColor = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  return (
    text === "transparent" ||
    text === "currentcolor" ||
    text === "inherit" ||
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(text) ||
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^hsla?\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%(?:\s*,\s*(0|1|0?\.\d+))?\s*\)$/i.test(
      text,
    ) ||
    /^[a-z]+$/i.test(text)
  );
};

const isSafeIndentSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (text === "0") return true;

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const maximumByUnit = {
    px: 160,
    rem: 10,
    em: 10,
  };

  return amount >= 0 && amount <= maximumByUnit[unit];
};

const isSafeFontSize = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  const match = text.match(/^(\d+(?:\.\d+)?)(px|pt|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 64 },
    pt: { min: 6, max: 48 },
    rem: { min: 0.5, max: 4 },
    em: { min: 0.5, max: 4 },
    "%": { min: 50, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const isSafeFontFamily = (value) => {
  const text = String(value || "").trim();

  if (!text || text.length > 120) return false;

  if (
    text.includes(";") ||
    text.includes("{") ||
    text.includes("}") ||
    text.includes("\\") ||
    /url\s*\(/i.test(text) ||
    /expression\s*\(/i.test(text) ||
    /var\s*\(/i.test(text)
  ) {
    return false;
  }

  return /^[-"'a-z0-9À-ÿ_,\s]+$/i.test(text);
};

const isSafeLineHeight = (value) => {
  const text = String(value || "")
    .trim()
    .toLowerCase();

  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const amount = Number(text);

    return amount >= 0.8 && amount <= 3;
  }

  const match = text.match(/^(\d+(?:\.\d+)?)(px|rem|em|%)$/);

  if (!match) return false;

  const amount = Number(match[1]);
  const unit = match[2];

  const limits = {
    px: { min: 8, max: 80 },
    rem: { min: 0.8, max: 5 },
    em: { min: 0.8, max: 5 },
    "%": { min: 80, max: 300 },
  };

  return amount >= limits[unit].min && amount <= limits[unit].max;
};

const sanitizeInlineStyle = (styleText = "") => {
  const ALLOWED_STYLES = {
    color: (value) => isColor(value),

    "background-color": (value) => isColor(value),

    "text-align": (value) =>
      ["left", "right", "center", "justify"].includes(
        String(value).trim().toLowerCase(),
      ),

    "font-weight": (value) =>
      /^(normal|bold|bolder|lighter|[1-9]00)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-style": (value) =>
      /^(normal|italic|oblique)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "font-size": (value) => isSafeFontSize(value),

    "font-family": (value) => isSafeFontFamily(value),

    "line-height": (value) => isSafeLineHeight(value),

    "text-decoration": (value) =>
      /^(none|underline|line-through|overline)$/.test(
        String(value).trim().toLowerCase(),
      ),

    "margin-left": (value) => isSafeIndentSize(value),

    "padding-left": (value) => isSafeIndentSize(value),

    "text-indent": (value) => isSafeIndentSize(value),

    "list-style-type": (value) =>
      [
        "none",
        "disc",
        "circle",
        "square",
        "decimal",
        "lower-alpha",
        "upper-alpha",
        "lower-roman",
        "upper-roman",
      ].includes(String(value).trim().toLowerCase()),
  };

  return String(styleText)
    .split(";")
    .map((rule) => rule.trim())
    .filter(Boolean)
    .map((rule) => {
      const separatorIndex = rule.indexOf(":");

      if (separatorIndex === -1) return null;

      const property = rule
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();

      const value = rule.slice(separatorIndex + 1).trim();

      return ALLOWED_STYLES[property]?.(value)
        ? `${property}: ${value}`
        : null;
    })
    .filter(Boolean)
    .join("; ");
};

if (purify) {
  purify.removeAllHooks();

  purify.addHook("uponSanitizeAttribute", (node, data) => {
    if (data.attrName === "style") {
      const cleanStyle = sanitizeInlineStyle(data.attrValue);

      if (cleanStyle) {
        data.attrValue = cleanStyle;
      } else {
        data.keepAttr = false;
      }
    }
  });

  purify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;

    const href = node.getAttribute("href") || "";

    if (/^\s*javascript:/i.test(href)) {
      node.removeAttribute("href");
    }

    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

/* -------------------------------------------------------
   Classes communes au contenu enrichi
-------------------------------------------------------- */

const RICH_TEXT_CLASSES = cn(
  "gallery-rich-content",
  "prose prose-slate max-w-none break-words",
  "text-sm leading-7 text-slate-600",
  "dark:prose-invert dark:text-slate-300",

  // Paragraphes
  "prose-p:my-3",
  "[&_p]:whitespace-pre-wrap",

  // Titres
  "prose-headings:my-3",
  "prose-headings:font-barlow",
  "prose-headings:font-bold",
  "prose-headings:text-slate-950",
  "dark:prose-headings:!text-white",

  // Texte enrichi
  "prose-strong:text-slate-950",
  "prose-b:text-slate-950",
  "dark:prose-strong:!text-white",
  "dark:prose-b:!text-white",
  "prose-a:font-bold",
  "prose-a:text-primary",
  "hover:prose-a:underline",

  // Listes
  "[&_ul]:!my-4",
  "[&_ul]:!list-disc",
  "[&_ul]:!pl-6",
  "[&_ol]:!my-4",
  "[&_ol]:!list-decimal",
  "[&_ol]:!pl-6",
  "[&_li]:!my-1",
  "[&_li]:!pl-1",
  "[&_li]:whitespace-pre-wrap",

  // Sous-listes
  "[&_ul_ul]:!my-2",
  "[&_ul_ul]:!list-[circle]",
  "[&_ul_ul]:!pl-5",
  "[&_ul_ul_ul]:!list-[square]",
  "[&_ol_ol]:!my-2",
  "[&_ol_ol]:!list-[lower-alpha]",
  "[&_ol_ol]:!pl-5",
  "[&_ol_ol_ol]:!list-[lower-roman]",

  // Alignements Quill
  "[&_.ql-align-center]:!text-center",
  "[&_.ql-align-right]:!text-right",
  "[&_.ql-align-justify]:!text-justify",

  // Indentations Quill
  "[&_.ql-indent-1]:!ml-6",
  "[&_.ql-indent-2]:!ml-12",
  "[&_.ql-indent-3]:!ml-16",
  "[&_.ql-indent-4]:!ml-20",
  "[&_.ql-indent-5]:!ml-24",

  // Tailles Quill
  "[&_.ql-size-small]:!text-xs",
  "[&_.ql-size-large]:!text-xl",
  "[&_.ql-size-huge]:!text-3xl",

  // Polices Quill
  "[&_.ql-font-serif]:!font-serif",
  "[&_.ql-font-monospace]:!font-mono",

  // Citations
  "prose-blockquote:my-4",
  "prose-blockquote:border-primary",
  "prose-blockquote:bg-orange-50",
  "prose-blockquote:px-4",
  "prose-blockquote:py-2",
  "prose-blockquote:not-italic",
  "dark:prose-blockquote:bg-orange-500/10",

  // Images intégrées
  "prose-img:mx-auto",
  "prose-img:rounded-xl",
  "prose-img:shadow-md",

  // Séparateurs
  "prose-hr:border-slate-200",
  "dark:prose-hr:border-white/10",
  "prose-code:break-words",

  // Marges externes
  "[&>*:first-child]:mt-0",
  "[&>*:last-child]:mb-0",
);

/* -------------------------------------------------------
   Helpers
-------------------------------------------------------- */

function sanitizeHtml(html = "") {
  if (!purify) return "";

  return purify.sanitize(String(html || ""), {
    USE_PROFILES: {
      html: true,
    },
    ADD_ATTR: [
      "style",
      "target",
      "rel",
      "class",
      "data-list",
    ],
    ALLOW_DATA_ATTR: true,
    FORBID_TAGS: ["style"],
  });
}

function stripHtml(html = "") {
  if (typeof window === "undefined") {
    return String(html || "")
      .replace(/<[^>]*>/g, "")
      .trim();
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  return (div.textContent || div.innerText || "").trim();
}

function isActive(value) {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return true;
  }

  return (
    value === true ||
    value === 1 ||
    value === "1"
  );
}

function resolvePhoto(url) {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return FALLBACK;

  return toAbsPhoto(cleanUrl) || FALLBACK;
}

function handleImageError(event) {
  const image = event?.currentTarget;

  if (!image) return;

  if (image.dataset.fallbackApplied === "true") return;

  image.dataset.fallbackApplied = "true";
  image.src = FALLBACK;
}

/* -------------------------------------------------------
   États de chargement
-------------------------------------------------------- */

function GallerySkeletonCard() {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="h-64 animate-pulse bg-slate-200 dark:bg-slate-800" />

      <div className="space-y-4 p-5">
        <div className="h-5 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />

        <div className="h-4 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />

        <div className="h-4 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="col-span-full rounded-[2rem] border border-dashed border-slate-300 bg-white/70 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
        <FaImages />
      </div>

      <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        Aucun album photo disponible
      </h3>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
        Les albums du club apparaîtront ici dès qu’ils seront
        publiés dans l’administration.
      </p>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="col-span-full rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
      <div className="flex items-center justify-center gap-3">
        <FaExclamationTriangle />
        {message}
      </div>
    </div>
  );
}

/* -------------------------------------------------------
   Carte d’un album
-------------------------------------------------------- */

function GalleryCard({
  gallery,
  photos,
  cover,
  index,
  user,
  onPreview,
  onOpenAlbum,
}) {
  const [isDescriptionTruncated, setIsDescriptionTruncated] =
    useState(false);

  const descriptionRef = useRef(null);

  const previewPhotos = photos.slice(0, 4);

  const descriptionHtml = useMemo(() => {
    const rawDescription = gallery?.description || "";
    const plainDescription = stripHtml(rawDescription);

    if (!plainDescription) {
      return sanitizeHtml(
        "<p><em>Revivez les moments forts du club à travers cet album.</em></p>",
      );
    }

    return sanitizeHtml(rawDescription);
  }, [gallery?.description]);

  useLayoutEffect(() => {
    const element = descriptionRef.current;

    if (!element) {
      setIsDescriptionTruncated(false);
      return undefined;
    }

    let animationFrameId;

    const checkOverflow = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        setIsDescriptionTruncated(
          element.scrollHeight > element.clientHeight + 2,
        );
      });
    };

    checkOverflow();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(checkOverflow)
        : null;

    resizeObserver?.observe(element);
    window.addEventListener("resize", checkOverflow);

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      resizeObserver?.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [descriptionHtml, gallery?.id]);

  return (
    <Reveal variant="zoom" delay={index * 90}>
      <article className="group relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-2 hover:shadow-2xl dark:border-white/10 dark:bg-slate-900">
        <button
          type="button"
          onClick={() => onPreview(gallery)}
          className="relative block h-72 w-full overflow-hidden text-left"
          aria-label={`Prévisualiser l'album ${
            gallery?.titre || ""
          }`}
          title="Prévisualiser l’album"
        >
          <img
            src={cover}
            alt={gallery?.titre || "Album photo"}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
            onError={handleImageError}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/25 to-transparent" />

          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary/25 opacity-0 transition duration-500 group-hover:opacity-100" />

          <div className="absolute left-4 top-4 z-20 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/95 px-4 py-2 text-xs font-extrabold text-slate-950 shadow-xl backdrop-blur-xl">
            <FiImage className="text-primary" />

            {photos.length} photo
            {photos.length > 1 ? "s" : ""}
          </div>

          <div className="absolute right-4 top-4 z-20 flex h-12 w-12 items-center justify-center rounded-full border border-white/40 bg-white/95 text-slate-950 shadow-2xl backdrop-blur-xl transition group-hover:bg-primary group-hover:text-white">
            <FiEye className="text-lg" />
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-5">
            <h3 className="font-barlow text-2xl font-extrabold leading-tight text-white drop-shadow-lg">
              {gallery?.titre || "Album du club"}
            </h3>
          </div>
        </button>

        <div className="flex flex-1 flex-col p-5 pb-7">
          {/* Description enrichie limitée */}

          <div className="relative min-h-[105px]">
            <div
              ref={descriptionRef}
              className={cn(
                RICH_TEXT_CLASSES,
                "max-h-[125px] overflow-hidden pr-1",
                "[&_img]:hidden",
                "[&_iframe]:hidden",
                "[&_video]:hidden",
              )}
              style={{ tabSize: 4 }}
              dangerouslySetInnerHTML={{
                __html: descriptionHtml,
              }}
            />

            {isDescriptionTruncated ? (
              <div
                className={cn(
                  "pointer-events-none absolute inset-x-0 bottom-0 h-16",
                  "bg-gradient-to-t from-white via-white/95 to-transparent",
                  "dark:from-slate-900 dark:via-slate-900/95",
                )}
                aria-hidden="true"
              />
            ) : null}
          </div>

          {previewPhotos.length > 1 ? (
            <div className="mt-5 grid grid-cols-4 gap-2">
              {previewPhotos.map((photo, photoIndex) => (
                <button
                  key={
                    photo?.id ||
                    `${gallery?.id}-${photoIndex}`
                  }
                  type="button"
                  onClick={() =>
                    onPreview(gallery, photoIndex)
                  }
                  className="relative h-16 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 transition hover:-translate-y-0.5 hover:shadow dark:border-white/10 dark:bg-slate-800"
                  aria-label={`Ouvrir la photo ${
                    photoIndex + 1
                  }`}
                >
                  <img
                    src={resolvePhoto(photo?.photo_url)}
                    alt={
                      photo?.titre ||
                      gallery?.titre ||
                      "Photo"
                    }
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={handleImageError}
                  />
                </button>
              ))}
            </div>
          ) : null}

          <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-5">
            <button
              type="button"
              onClick={() => onPreview(gallery)}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-5 py-3 text-sm font-bold text-primary transition hover:-translate-y-0.5 hover:bg-primary hover:text-white"
            >
              Prévisualiser
              <FiMaximize2 className="text-sm" />
            </button>

            {user ? (
              <button
                type="button"
                onClick={() => onOpenAlbum(gallery.id)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
              >
                Voir l’album
                <FaArrowRight className="text-xs" />
              </button>
            ) : null}
          </div>
        </div>

        <div
          aria-hidden="true"
          className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-90 transition group-hover:opacity-100"
        />
      </article>
    </Reveal>
  );
}

/* -------------------------------------------------------
   Modale de prévisualisation de l’album
-------------------------------------------------------- */

function AlbumPreviewModal({
  gallery,
  photos,
  user,
  onClose,
  onOpenAlbum,
  onOpenLightbox,
}) {
  const hasMorePhotos =
    photos.length > MAX_PREVIEW_PHOTOS;

  const previewPhotos = photos.slice(
    0,
    MAX_PREVIEW_PHOTOS,
  );

  const safeDescription = useMemo(() => {
    const rawDescription = gallery?.description || "";

    if (!stripHtml(rawDescription)) {
      return "";
    }

    return sanitizeHtml(rawDescription);
  }, [gallery?.description]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  if (!gallery) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/75 px-3 py-4 backdrop-blur-sm sm:px-6 sm:py-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Prévisualisation de l’album ${
        gallery?.titre || "Album photo"
      }`}
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-white shadow-2xl dark:bg-slate-950"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 p-4 backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/95 sm:p-6">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-lg transition hover:text-primary dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
            aria-label="Fermer l’album"
          >
            <FiX className="text-xl" />
          </button>

          <div className="pr-14">
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
              <FaCameraRetro />
              Prévisualisation
            </div>

            <h3 className="mt-4 font-barlow text-2xl font-extrabold text-slate-950 dark:text-white sm:text-3xl">
              {gallery?.titre || "Album photo"}
            </h3>

            <p className="mt-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
              {photos.length} photo
              {photos.length > 1 ? "s" : ""}

              {hasMorePhotos
                ? ` — ${MAX_PREVIEW_PHOTOS} affichées en aperçu`
                : ""}
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {/* Description complète enrichie */}

          {safeDescription ? (
            <div className="mb-6 rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5 sm:p-5">
              <div
                className={RICH_TEXT_CLASSES}
                style={{ tabSize: 4 }}
                dangerouslySetInnerHTML={{
                  __html: safeDescription,
                }}
              />
            </div>
          ) : null}

          {previewPhotos.length === 0 ? (
            <div className="rounded-[2rem] border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm italic text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
              Aucune photo dans cet album.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {previewPhotos.map((photo, index) => (
                <button
                  key={photo?.id || index}
                  type="button"
                  onClick={() => onOpenLightbox(index)}
                  className="group relative h-36 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm transition hover:-translate-y-1 hover:shadow-lg dark:border-white/10 dark:bg-slate-800 sm:h-44"
                  aria-label={`Ouvrir la photo ${
                    index + 1
                  }`}
                >
                  <img
                    src={resolvePhoto(photo?.photo_url)}
                    alt={
                      photo?.titre ||
                      gallery?.titre ||
                      "Photo"
                    }
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    loading="lazy"
                    onError={handleImageError}
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/55 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />

                  <div className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full border border-white/30 bg-white/90 text-slate-950 opacity-0 shadow-lg backdrop-blur transition group-hover:opacity-100">
                    <FiMaximize2 />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/95 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
              Pour éviter les problèmes d’affichage sur mobile,
              la prévisualisation est volontairement limitée.
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
              >
                Fermer
              </button>

              {user ? (
                <button
                  type="button"
                  onClick={() =>
                    onOpenAlbum(gallery.id)
                  }
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:brightness-110"
                >
                  Voir toutes les photos
                  <FaArrowRight className="text-xs" />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------
   Lightbox
-------------------------------------------------------- */

function Lightbox({
  gallery,
  photos,
  index,
  onClose,
  onPrev,
  onNext,
}) {
  const currentPhoto = photos[index];

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") onPrev();
      if (event.key === "ArrowRight") onNext();
    };

    window.addEventListener("keydown", onKeyDown);

    return () =>
      window.removeEventListener("keydown", onKeyDown);
  }, [onClose, onPrev, onNext]);

  if (!currentPhoto) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/90 p-3 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Photo ${index + 1} sur ${photos.length}`}
    >
      <div
        className="relative w-full max-w-6xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute -right-2 -top-2 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/90 text-slate-900 shadow-xl transition hover:text-primary"
          aria-label="Fermer"
        >
          <FiX className="text-xl" />
        </button>

        {photos.length > 1 ? (
          <>
            <button
              type="button"
              onClick={onPrev}
              className="absolute left-2 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow-xl transition hover:text-primary sm:left-4"
              aria-label="Photo précédente"
            >
              <FiChevronLeft className="text-xl" />
            </button>

            <button
              type="button"
              onClick={onNext}
              className="absolute right-2 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow-xl transition hover:text-primary sm:right-4"
              aria-label="Photo suivante"
            >
              <FiChevronRight className="text-xl" />
            </button>
          </>
        ) : null}

        <img
          src={resolvePhoto(currentPhoto?.photo_url)}
          alt={
            currentPhoto?.titre ||
            gallery?.titre ||
            "Photo"
          }
          className="max-h-[82vh] w-full rounded-[2rem] border border-white/15 bg-black/20 object-contain shadow-2xl"
          onError={handleImageError}
        />

        <div className="mt-4 flex items-center justify-center gap-3 text-sm font-bold text-white/85">
          <span>
            {index + 1} / {photos.length}
          </span>

          {currentPhoto?.titre ? (
            <span className="hidden text-white/60 sm:inline">
              — {currentPhoto.titre}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------
   Composant principal
-------------------------------------------------------- */

export default function LastGallerie() {
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] =
    useState(null);
  const [lightboxOpen, setLightboxOpen] =
    useState(false);
  const [lightboxIndex, setLightboxIndex] =
    useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const navigate = useNavigate();
  const { user } = useUser();

  const orderPhotos = useCallback((photos = []) => {
    const array = Array.isArray(photos)
      ? [...photos]
      : [];

    array.sort((photoA, photoB) => {
      const positionA = Number.isFinite(
        Number(photoA?.position),
      )
        ? Number(photoA.position)
        : Number.MAX_SAFE_INTEGER;

      const positionB = Number.isFinite(
        Number(photoB?.position),
      )
        ? Number(photoB.position)
        : Number.MAX_SAFE_INTEGER;

      return (
        positionA -
          positionB ||
        (photoA?.id ?? 0) - (photoB?.id ?? 0)
      );
    });

    return array.filter((photo) =>
      isActive(photo?.is_active),
    );
  }, []);

  const getCoverPhoto = useCallback(
    (gallery) => {
      const ordered = orderPhotos(
        gallery?.photos || [],
      );

      return (
        ordered.find((photo) =>
          isActive(photo?.is_cover) &&
          (
            photo?.is_cover === true ||
            photo?.is_cover === 1 ||
            photo?.is_cover === "1"
          ),
        ) ||
        ordered[0] ||
        null
      );
    },
    [orderPhotos],
  );

  useEffect(() => {
    let mounted = true;

    const fetchGalleries = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const all = await getAllGalleries();

        const active = (
          Array.isArray(all) ? all : []
        )
          .filter((gallery) =>
            isActive(gallery?.is_active),
          )
          .slice(0, MAX_HOME_ALBUMS);

        if (!mounted) return;

        setGalleries(active);
      } catch (error) {
        console.error(
          "Failed to fetch galleries",
          error,
        );

        if (mounted) {
          setGalleries([]);
          setErrorMsg(
            "Impossible de charger les albums photos.",
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchGalleries();

    return () => {
      mounted = false;
    };
  }, []);

  const selectedPhotos = useMemo(() => {
    return orderPhotos(
      selectedGallery?.photos || [],
    );
  }, [selectedGallery, orderPhotos]);

  const previewLightboxPhotos = useMemo(() => {
    return selectedPhotos.slice(
      0,
      MAX_PREVIEW_PHOTOS,
    );
  }, [selectedPhotos]);

  const openAlbumPreview = (
    gallery,
    startIndex = 0,
  ) => {
    setSelectedGallery(gallery);
    setLightboxIndex(startIndex);
    setLightboxOpen(false);
  };

  const closeAlbumPreview = () => {
    setSelectedGallery(null);
    setLightboxOpen(false);
    setLightboxIndex(0);
  };

  const openLightbox = (index = 0) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const prevPhoto = useCallback(() => {
    const total = previewLightboxPhotos.length;

    if (!total) return;

    setLightboxIndex(
      (currentIndex) =>
        (currentIndex - 1 + total) % total,
    );
  }, [previewLightboxPhotos.length]);

  const nextPhoto = useCallback(() => {
    const total = previewLightboxPhotos.length;

    if (!total) return;

    setLightboxIndex(
      (currentIndex) =>
        (currentIndex + 1) % total,
    );
  }, [previewLightboxPhotos.length]);

  const handleOpenAlbumPage = (galleryId) => {
    navigate(`/galerie-photos/album/${galleryId}`);
  };

  return (
    <section
      id="albums"
      className="relative scroll-mt-[calc(var(--site-navbar-h)+24px)] overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaCameraRetro />
            Galerie du club
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Derniers{" "}
            <span className="text-primary">
              albums photo
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Revivez les entraînements, compétitions,
            événements et moments forts du club.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {loading
            ? Array.from({ length: 3 }).map(
                (_, index) => (
                  <GallerySkeletonCard
                    key={`gallery-skeleton-${index}`}
                  />
                ),
              )
            : null}

          {!loading && errorMsg ? (
            <ErrorState message={errorMsg} />
          ) : null}

          {!loading &&
          !errorMsg &&
          galleries.length === 0 ? (
            <EmptyState />
          ) : null}

          {!loading && !errorMsg
            ? galleries.map((gallery, index) => {
                const photos = orderPhotos(
                  gallery?.photos || [],
                );

                const cover =
                  getCoverPhoto(gallery);

                const coverSrc =
                  cover?.photo_url
                    ? resolvePhoto(cover.photo_url)
                    : FALLBACK;

                return (
                  <GalleryCard
                    key={gallery.id || index}
                    gallery={gallery}
                    photos={photos}
                    cover={coverSrc}
                    index={index}
                    user={user}
                    onPreview={openAlbumPreview}
                    onOpenAlbum={
                      handleOpenAlbumPage
                    }
                  />
                );
              })
            : null}
        </div>
      </div>

      {selectedGallery ? (
        <AlbumPreviewModal
          gallery={selectedGallery}
          photos={selectedPhotos}
          user={user}
          onClose={closeAlbumPreview}
          onOpenAlbum={handleOpenAlbumPage}
          onOpenLightbox={openLightbox}
        />
      ) : null}

      {selectedGallery &&
      lightboxOpen &&
      previewLightboxPhotos.length > 0 ? (
        <Lightbox
          gallery={selectedGallery}
          photos={previewLightboxPhotos}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevPhoto}
          onNext={nextPhoto}
        />
      ) : null}
    </section>
  );
}